<template>
  <div class="v-badge" :class="classes">
    <slot />

    <sup v-if="!hidden" class="v-badge__content" :style v-text="value" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { VBadgeType } from '../types.ts'

interface Props {
  value?: string | number
  size?: number
  border?: boolean
  hidden?: boolean
  type?: VBadgeType
}

const props = withDefaults(defineProps<Props>(), {
  value: '',
  size: 15,
  border: false,
  hidden: false,
  type: 'red',
})

const slots = defineSlots<{
  default?(): any
}>()

const classes = computed(() => ({
  'has-slot': slots.default,
  'has-border': props.border,
  [`v-badge--${props.type}`]: true,
  'is-hidden': props.hidden,
}))

const style = computed(() => {
  const size = `${props.size}px`
  const padding = props.size > 4.5 ? 4.5 : props.size

  return {
    borderRadius: `${props.size * 2}px`,
    height: size,
    minWidth: size,
    lineHeight: size,
    padding: `${padding}px`,
  }
})

defineExpose({
  style,
})
</script>

<style>
.v-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.v-badge__content {
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  white-space: nowrap;
  top: 0;
}

.has-border .v-badge__content {
  border: 2px solid #fff;
}

.v-badge--red .v-badge__content {
  background-color: var(--color-red);
}

.v-badge--orange .v-badge__content {
  background-color: var(--color-orange);
}

.v-badge--green .v-badge__content {
  background-color: var(--color-green);
}

.v-badge--primary .v-badge__content {
  background-color: var(--color-primary);
}

.v-badge--secondary .v-badge__content {
  background-color: var(--color-secondary);
}

.v-badge--info .v-badge__content {
  background-color: var(--color-grey-10);
  color: #000;
}

.v-badge.has-slot .v-badge__content {
  position: absolute;
  top: 0;
  right: 10px;
  transform: translateY(-50%) translateX(100%);
}
</style>
